/**
 * The question a run that holds a video asks once (PRD-0115 SC-001): process the sound of its
 * videos too, or the picture only. The two answers stand side by side with equal weight — a
 * camera on a car park has nothing worth transcribing, a questioning filmed on a phone has little
 * else — and nothing is preselected: the officer chooses. Pure; the run that asks owns the answer.
 */
import { AudioLines, Clapperboard } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

const COPY = 'documents.video.sound'

/** What a run asks the question about: its videos, or folders that may hold some. */
export interface VideoSoundScope {
  /** The run names folders, whose contents the server resolves: they may hold videos. */
  folders?: boolean
  /** One video analysed before: the re-run warning stands beside the question (ADR-0089). */
  rerun?: boolean
}

function Hint({ folders }: { folders?: boolean }) {
  const { t } = useTranslation()
  return (
    <p className="text-sm text-muted-foreground">
      {t(folders ? `${COPY}.bodyFolders` : `${COPY}.body`)} {t(`${COPY}.applies`)}
    </p>
  )
}

/** The question as a field of a dialog that already opens: the run configuration of a selection. */
export function VideoSoundChoice({
  value,
  onChange,
  folders,
}: {
  value: boolean | null
  onChange: (sound: boolean) => void
  folders?: boolean
}) {
  const { t } = useTranslation()
  const id = useId()
  const options = [
    { sound: true, label: `${COPY}.yes`, hint: `${COPY}.yesHint`, Icon: AudioLines },
    { sound: false, label: `${COPY}.no`, hint: `${COPY}.noHint`, Icon: Clapperboard },
  ] as const
  return (
    <fieldset className="space-y-2" data-testid="video-sound-choice">
      <legend className="text-xs uppercase tracking-wide text-muted-foreground">
        {t(`${COPY}.field`)}
      </legend>
      <Hint folders={folders} />
      <RadioGroup
        value={value === null ? '' : String(value)}
        onValueChange={next => onChange(next === 'true')}
        className="grid gap-2 sm:grid-cols-2"
      >
        {options.map(({ sound, label, hint, Icon }) => (
          <Label
            key={label}
            htmlFor={`${id}-${sound}`}
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
          >
            <RadioGroupItem id={`${id}-${sound}`} value={String(sound)} className="mt-0.5" />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                {t(label)}
              </span>
              <span className="text-xs font-normal text-muted-foreground">{t(hint)}</span>
            </span>
          </Label>
        ))}
      </RadioGroup>
      {value === null && <p className="text-xs text-muted-foreground">{t(`${COPY}.required`)}</p>}
    </fieldset>
  )
}

/**
 * The question on its own, before a run that opens no dialog of its own starts: *Run pending*,
 * *Retry failed*, *Analyse video* on a row. Closing it starts nothing.
 */
export function VideoSoundDialog({
  scope,
  onAnswer,
  onCancel,
}: {
  /** Null while nothing is asked. */
  scope: VideoSoundScope | null
  onAnswer: (sound: boolean) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog
      open={scope !== null}
      onOpenChange={open => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="video-sound-dialog">
        <DialogHeader>
          <DialogTitle>
            {scope?.rerun ? t('documents.video.analyseAgainTitle') : t(`${COPY}.title`)}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              {scope?.rerun && (
                <p className="text-sm text-muted-foreground">
                  {t('documents.video.analyseAgainWarning')} {t(`${COPY}.rerun`)}
                </p>
              )}
              {scope?.rerun && (
                <p className="text-sm font-medium text-foreground">{t(`${COPY}.title`)}</p>
              )}
              <Hint folders={scope?.folders} />
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" className="gap-2" onClick={() => onAnswer(false)}>
              <Clapperboard className="size-4" aria-hidden />
              {t(`${COPY}.no`)}
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => onAnswer(true)}>
              <AudioLines className="size-4" aria-hidden />
              {t(`${COPY}.yes`)}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
